import { useState } from 'react';
import { Box, Grid, Typography, Popover, Button, FormControlLabel, InputLabel, TextField, Checkbox } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { Formik, Form, Field } from 'formik';

interface FileComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
}

export default function FileComponent({ values, index, childIndex, setFieldValue, onSubmit }: FileComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-file-popover' : undefined;

  const child = values.fields[index].children[childIndex];
  const settings = child.settings || {};
  const answer = child.answer || [];
  const maxImages = parseInt(settings.maxImages || 1);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const limitedFiles = files.slice(0, maxImages);
    setFieldValue(`fields.${index}.children.${childIndex}.answer`, limitedFiles);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <InputLabel sx={{ color: 'black' }}>{child.question}</InputLabel>
        </Grid>

        <Grid item xs={11}>
          <Button variant="outlined" component="label" fullWidth size="small">
            {answer.length > 0 ? `${answer.length} file(s) selected` : 'Upload File(s)'}
            <input type="file" hidden multiple={maxImages > 1} accept={settings.accept || '*'} onChange={handleFileChange} />
          </Button>
        </Grid>

        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
            <CreateIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Formik
          initialValues={{
            question: child.question || '',
            description: child.description || '',
            accept: settings.accept || '',
            maxImages: settings.maxImages || 1,
            required: settings.required || false
          }}
          onSubmit={(formValues) => {
            const updatedChild = {
              ...child,
              question: formValues.question,
              description: formValues.description,
              settings: {
                ...settings,
                accept: formValues.accept,
                maxImages: formValues.maxImages,
                required: formValues.required
              }
            };
            setFieldValue(`fields.${index}.children.${childIndex}`, updatedChild);
            handleClosePopover();
          }}
        >
          {({ values: formValues, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit File Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <InputLabel>Label</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="question" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Description</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="description" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Allowed Types</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="accept" placeholder="e.g. image/* or .png,.jpg" fullWidth size="small" />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>No. of Images</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="maxImages" type="number" fullWidth size="small" />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={formValues.required} onChange={(e) => setFieldValue('required', e.target.checked)} />}
                    label="Required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
